/**
 * CustomerList - Responsive component for displaying customers
 * Mobile/Tablet: Cards
 * Desktop: Simplified table
 */

import React, { useMemo } from "react";
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  ButtonBase,
} from "@mui/material";
import { Tag } from "antd";
import DataTable from "../../../shared/components/DataTable";
import CustomerCard from "./CustomerCard";
import { useDeviceType } from "../../leads/hooks/useDeviceType";
import {
  formatPhoneDisplay,
  formatLastActive,
  getCustomerProfileId,
} from "../utils/customerUtils";

const CustomerList = ({
  customers = [],
  loading = false,
  onViewCustomer,
  onRefresh,
}) => {
  const { isMobile, isTablet, isDesktop } = useDeviceType();

  // Desktop table columns (simplified)
  const columns = useMemo(
    () => [
      {
        title: "Customer",
        key: "customer",
        width: 250,
        render: (_, record) => (
          <Box
            component={onViewCustomer ? ButtonBase : "div"}
            onClick={
              onViewCustomer
                ? (event) => {
                    event.stopPropagation();
                    onViewCustomer(record);
                  }
                : undefined
            }
            sx={{
              display: "block",
              textAlign: "left",
              borderRadius: 1,
              width: "100%",
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {record.name || "Unnamed Customer"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatPhoneDisplay(record.phone) || "No phone"}
            </Typography>
          </Box>
        ),
      },
      {
        title: "Contact",
        key: "contact",
        width: 180,
        render: (_, record) => {
          const phone = formatPhoneDisplay(record.phone);
          const email = record.email || null;
          return (
            <Box>
              {phone && (
                <Typography variant="body2">{phone}</Typography>
              )}
              {email && (
                <Typography variant="caption" color="text.secondary">
                  {email}
                </Typography>
              )}
              {!phone && !email && (
                <Typography variant="caption" color="text.secondary">
                  No contact info
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        title: "Activity",
        key: "activity",
        width: 120,
        render: (_, record) => {
          const activityCount = record.activityCount || 0;
          return (
            <Tag color={activityCount > 0 ? "blue" : "default"}>
              {activityCount} {activityCount === 1 ? "item" : "items"}
            </Tag>
          );
        },
      },
      {
        title: "Status",
        key: "status",
        width: 100,
        render: (_, record) => {
          const isActive = record.status === "active";
          return (
            <Tag color={isActive ? "green" : "default"}>
              {isActive ? "Active" : "Dormant"}
            </Tag>
          );
        },
      },
      {
        title: "Last Active",
        key: "lastActive",
        width: 140,
        render: (_, record) => {
          const lastActive = record.lastActivityDate || record.updatedAt;
          return (
            <Typography variant="body2" color="text.secondary">
              {formatLastActive(lastActive)}
            </Typography>
          );
        },
      },
    ],
    [onViewCustomer]
  );

  // Mobile/Tablet: Card view
  if (isMobile || isTablet) {
    return (
      <Box>
        {loading ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Loading customers...</Typography>
          </Box>
        ) : customers.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography color="text.secondary">
              No customers found
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {customers.map((customer) => (
              <Grid item xs={12} sm={isTablet ? 6 : 12} key={customer.customerId}>
                <CustomerCard
                  customer={customer}
                  onView={onViewCustomer}
                  dense={isTablet}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    );
  }

  // Desktop: Table view
  return (
    <DataTable
      data={customers}
      columns={columns}
      loading={loading}
      title="Customers"
      searchPlaceholder="Search customers by name, phone, or email..."
      searchFields={["name", "phone", "email"]}
      onRefresh={onRefresh}
      onRowClick={onViewCustomer}
      rowKey={(customer) =>
        getCustomerProfileId(customer) ||
        customer.phone ||
        customer.email ||
        customer.name
      }
      pageSize={50}
      showRefresh={false} // Refresh handled in header
    />
  );
};

export default CustomerList;

